import React, { useEffect, useState } from "react";
import { Users, Copy, Check } from "lucide-react";
import Layout from "../components/Layout";
import { apiRequest } from "../api";

export default function Referrals() {
  const [referrals, setReferrals] = useState([]);
  const [referralLink, setReferralLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const loadReferrals = async () => {
    try {
      setError("");

      const data = await apiRequest("/users/me/referrals");

      setReferrals(
        Array.isArray(data)
          ? data
          : Array.isArray(data?.referrals)
          ? data.referrals
          : []
      );

      if (data?.referralLink) {
        setReferralLink(data.referralLink);
      } else if (data?.referral_code) {
        setReferralLink(
          `${window.location.origin}/login?ref=${data.referral_code}`
        );
      }
    } catch (err) {
      setError(err.message || "Unable to load referrals.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferrals();

    const interval = setInterval(() => {
      loadReferrals();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const copyReferralLink = async () => {
    if (!referralLink) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      setError("Unable to copy referral link.");
    }
  };

  return (
    <Layout title="Referrals">
      <div className="rounded-2xl border border-[#1B2033] bg-[#0E1220] p-6">

        {/* HEADER */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-[#5B8DEF]" />

            <div>
              <h2 className="font-semibold text-[15px]">
                Your Referrals
              </h2>

              <p className="text-[11px] text-[#7C8AA5] mt-1">
                Referrals update automatically.
              </p>
            </div>
          </div>

          <button
            onClick={loadReferrals}
            className="text-xs text-[#5B8DEF] hover:underline"
          >
            Refresh
          </button>
        </div>

        {/* REFERRAL LINK */}
        <div className="mb-5">
          <label className="block text-xs text-[#7C8AA5] mb-2">
            Your Referral Link
          </label>

          <div className="flex items-center gap-2 bg-[#161B2C] border border-[#2A3149] rounded-lg px-4 py-2.5">

            <span className="text-sm text-[#B9C1D9] flex-1 break-all">
              {referralLink || "Loading referral link..."}
            </span>

            <button
              onClick={copyReferralLink}
              disabled={!referralLink}
              className="flex items-center gap-1.5 text-xs text-[#5B8DEF] font-medium hover:opacity-80 disabled:opacity-40 shrink-0"
            >
              {copied ? (
                <>
                  <Check size={14} />
                  Copied
                </>
              ) : (
                <>
                  <Copy size={14} />
                  Copy
                </>
              )}
            </button>
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div className="mb-4 rounded-lg bg-[#E5566B]/10 border border-[#E5566B]/20 text-[#E5566B] text-xs px-4 py-3">
            {error}
          </div>
        )}

        {/* LOADING */}
        {loading ? (
          <div className="text-xs text-[#7C8AA5] py-6 text-center">
            Loading referrals...
          </div>
        ) : referrals.length === 0 ? (
          <div className="text-xs text-[#7C8AA5] py-6 text-center">
            No referrals yet.
          </div>
        ) : (
          <>
            {/* DESKTOP TABLE */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[#7C8AA5] border-b border-[#1B2033]">
                    <th className="py-3">Name</th>
                    <th className="py-3">Joined</th>
                    <th className="py-3">Earned</th>
                    <th className="py-3">Status</th>
                  </tr>
                </thead>

                <tbody>
                  {referrals.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b border-[#161B2C]"
                    >
                      <td className="py-3">
                        {r.name || "Unknown"}
                      </td>

                      <td className="py-3 text-[#7C8AA5]">
                        {r.created_at
                          ? new Date(
                              r.created_at
                            ).toLocaleDateString()
                          : r.date || "-"}
                      </td>

                      <td className="py-3 text-[#3FBF8F]">
                        {Number(
                          r.earned || r.referral_earned || 0
                        ).toFixed(2)}{" "}
                        USDT
                      </td>

                      <td className="py-3">
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            r.status === "active" ||
                            r.status === "Active"
                              ? "bg-[#3FBF8F]/15 text-[#3FBF8F]"
                              : "bg-[#E0A63C]/15 text-[#E0A63C]"
                          }`}
                        >
                          {r.status || "Active"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* MOBILE CARDS */}
            <div className="md:hidden space-y-3">
              {referrals.map((r) => (
                <div
                  key={r.id}
                  className="rounded-xl border border-[#1B2033] bg-[#161B2C] p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-medium text-sm">
                      {r.name || "Unknown"}
                    </div>

                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        r.status === "active" ||
                        r.status === "Active"
                          ? "bg-[#3FBF8F]/15 text-[#3FBF8F]"
                          : "bg-[#E0A63C]/15 text-[#E0A63C]"
                      }`}
                    >
                      {r.status || "Active"}
                    </span>
                  </div>

                  <div className="text-xs text-[#7C8AA5]">
                    Joined:{" "}
                    {r.created_at
                      ? new Date(
                          r.created_at
                        ).toLocaleDateString()
                      : r.date || "-"}
                  </div>

                  <div className="text-xs text-[#3FBF8F] mt-2">
                    Earned:{" "}
                    {Number(
                      r.earned || r.referral_earned || 0
                    ).toFixed(2)}{" "}
                    USDT
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}